/**
 * WeeklyBarChart — renders weekly-aggregated data as vertical bars.
 * Best suited for sparse time-series data (<2 entries/day average).
 */

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { WeeklyDataPoint } from '@/utils/chart-adaptation';

interface WeeklyBarChartProps {
  data: WeeklyDataPoint[];
  color: string;
  gradientId: string;
  height?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderTooltip?: (props: any) => React.ReactNode;
}

const WeeklyBarChart: React.FC<WeeklyBarChartProps> = ({
  data,
  color,
  gradientId,
  height = 300,
  renderTooltip,
}) => {
  if (data.length === 0) {return null;}

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={1} />
            <stop offset="100%" stopColor={color} stopOpacity={0.6} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
        <XAxis
          dataKey="weekLabel"
          tick={{ fontSize: 11, fill: '#64748b' }}
          tickLine={{ stroke: '#cbd5e1' }}
          axisLine={{ stroke: '#cbd5e1' }}
          angle={-20}
          textAnchor="end"
          height={60}
        />
        <YAxis
          tick={{ fontSize: 12, fill: '#64748b' }}
          tickLine={{ stroke: '#cbd5e1' }}
          axisLine={{ stroke: '#cbd5e1' }}
          allowDecimals={false}
        />
        {renderTooltip ? (
          <Tooltip content={renderTooltip} />
        ) : (
          <Tooltip
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0,0,0,.1)',
            }}
            labelStyle={{ fontWeight: 600, color: '#1e293b', marginBottom: 4 }}
          />
        )}
        <Bar
          dataKey="total"
          name="Total"
          fill={`url(#${gradientId})`}
          radius={[8, 8, 0, 0]}
          animationDuration={300}
          animationEasing="ease-out"
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default WeeklyBarChart;
